// Construction lifecycle: blueprint → constructing → active, with worker auto-requests (§15.2).

import { buildingDef, unitDef } from '../../content/registry';
import { TUNING } from '../../content/tuning';
import { dist, type Vec2 } from '../../shared/vec';
import type { Sim } from '../sim';
import type { PlayerId } from '../../content/types';
import { buildingPassives } from './annexes';

const dt = TUNING.dt;

/** Construction-aura multiplier at pos (Royal Builder Guild — auras do not stack). */
function constructionAuraMult(sim: Sim, owner: PlayerId, pos: Vec2): number {
  let best = 1;
  for (const b of sim.state.buildings.values()) {
    if (b.owner !== owner || b.state !== 'active') continue;
    for (const passive of buildingPassives(buildingDef(b.defId))) {
      if (passive.kind !== 'constructionAura') continue;
      if (dist(b.pos, pos) <= passive.radius) best = Math.max(best, passive.buildMult);
    }
  }
  return best;
}

export function stepConstruction(sim: Sim): void {
  const st = sim.state;
  for (const b of st.buildings.values()) {
    if (b.state !== 'blueprint' && b.state !== 'constructing') continue;
    const bd = buildingDef(b.defId);

    // castle walls raise themselves once claimed (BattleForge-style)
    if (bd.selfBuilding) {
      b.state = 'constructing';
      b.buildProgress = Math.min(1, b.buildProgress + dt / bd.buildTime);
      b.health = Math.max(b.health, bd.maxHealth * (0.1 + 0.9 * b.buildProgress));
      if (b.buildProgress >= 1) {
        b.state = 'active';
        // walls stay passable during construction — collision lands only now
        sim.stampBuildingNav(b, 1);
        sim.pushEvent({ type: 'BuildingComplete', id: b.id, owner: b.owner, defId: b.defId });
      }
      continue;
    }

    // count builders on site and auto-request more (nearest idle/gathering workers)
    const activeBuilders: number[] = []; // per-builder speed multipliers (worker variants)
    let assigned = 0;
    for (const w of st.workers.values()) {
      if (w.owner === b.owner && w.buildTargetId === b.id && (w.state === 'building' || w.state === 'toBuild')) {
        assigned++;
        if (w.state === 'building') activeBuilders.push(unitDef(w.defId).workerVariant?.buildMult ?? 1);
      }
    }
    const builders = activeBuilders.length;
    // Royal Works: a fifth worker may join each build (diminishing returns still apply)
    const maxWorkers = TUNING.constructionMaxWorkers + sim.annexBonus(b.owner, 'extraBuilders');
    if (assigned < maxWorkers) {
      // request nearest eligible workers
      const eligible = [...st.workers.values()]
        .filter(
          (w) =>
            w.owner === b.owner &&
            w.buildTargetId === null &&
            (w.state === 'idle' || w.state === 'toDeposit' || w.state === 'gathering'),
        )
        // id tiebreak: equidistant workers must resolve to a total order, not to whatever the
        // sort's stability happens to preserve, or the client and the authoritative server can
        // assign different builders.
        .sort((a, z) => dist(a.pos, b.pos) - dist(z.pos, b.pos) || a.id - z.id);
      for (const w of eligible.slice(0, maxWorkers - assigned)) {
        w.buildTargetId = b.id;
        w.depositId = null;
        w.state = 'toBuild';
        w.path = null;
      }
    }

    if (builders > 0) {
      b.state = 'constructing';
      // diminishing extra-worker curve (§15.2), each builder scaled by its variant
      let rateMult = 0;
      for (let i = 0; i < builders; i++) rateMult += (TUNING.constructionDiminish[i] ?? 0.25) * activeBuilders[i];
      rateMult *= sim.annexMult(b.owner, 'buildSpeedMult');
      rateMult *= constructionAuraMult(sim, b.owner, b.pos);
      const progressGain = (rateMult / bd.buildTime) * dt;
      b.buildProgress = Math.min(1, b.buildProgress + progressGain);
      // health scales 10% → 100% with progress
      const targetHealth = bd.maxHealth * (0.1 + 0.9 * b.buildProgress);
      b.health = Math.max(b.health, Math.min(targetHealth, bd.maxHealth));
      if (b.buildProgress >= 1) {
        b.state = 'active';
        b.health = Math.max(b.health, bd.maxHealth * 0.98);
        sim.pushEvent({ type: 'BuildingComplete', id: b.id, owner: b.owner, defId: b.defId });
        // release builders
        for (const w of st.workers.values()) {
          if (w.buildTargetId === b.id) {
            w.buildTargetId = null;
            if (w.state === 'building' || w.state === 'toBuild') w.state = 'idle';
          }
        }
      }
    }
  }

  // disabled-state on site loss (A-08): production buildings at hostile/neutral sites disable
  for (const b of st.buildings.values()) {
    if (b.isCore || !b.siteId) continue;
    if (b.state !== 'active' && b.state !== 'disabled') continue;
    const site = st.sites.find((s) => s.id === b.siteId);
    const enabled = site && site.owner === b.owner;
    if (b.state === 'active' && !enabled) b.state = 'disabled';
    else if (b.state === 'disabled' && enabled) b.state = 'active';
  }
}
